import { motion } from "framer-motion";
import { Droplets, Wrench, ShowerHead, ArrowRight, Check } from "lucide-react";
import { Reveal, SectionLabel } from "@/lib/Reveal";
import { BUSINESS } from "@/lib/data";

const JOBS = [
  "Leaking & burst pipes",
  "Taps repaired or replaced",
  "Toilets not flushing",
  "New radiators supplied & fitted",
  "Showers, pumps & valves",
  "Outside taps fitted",
  "Overflows & ball valve faults",
  "Basins & sinks installed",
  "All domestic plumbing",
];

const PLUMB_IMG = "/images/work-tap.webp";

export const Plumbing = () => {
  return (
    <section id="plumbing" data-testid="plumbing-section" className="py-20 lg:py-28 bg-[#0C0C0C] border-y border-white/5 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div>
          <Reveal><SectionLabel>03 / Domestic plumbing</SectionLabel></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white mt-4">
              Leaking pipe to<br />new radiator.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-neutral-400 leading-relaxed max-w-md">
              It's not just gas. From a tap that won't stop dripping to a toilet that won't
              flush, we handle all domestic plumbing — small fixes done properly, bigger
              jobs quoted free and fair.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3.5" data-testid="plumbing-jobs-list">
              {JOBS.map((job) => (
                <li key={job} className="flex items-start gap-2.5 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-torch shrink-0 mt-0.5" strokeWidth={3} />
                  {job}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#quote"
                data-testid="plumbing-quote-btn"
                className="group bg-torch hover:bg-torch-hover text-white font-display font-bold uppercase tracking-wide px-7 py-3.5 rounded-sm flex items-center gap-3 transition-all hover:shadow-[0_0_32px_rgba(255,85,0,0.45)]"
              >
                Get a free plumbing quote
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href={BUSINESS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="plumbing-whatsapp-btn"
                className="border border-neutral-700 hover:border-torch text-neutral-200 hover:text-torch font-display font-bold uppercase tracking-wide px-7 py-3.5 rounded-sm transition-colors"
              >
                WhatsApp a photo of the problem
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="relative hidden lg:block">
          <div className="absolute -inset-3 border border-torch/40 -translate-x-5 -translate-y-5 pointer-events-none" />
          <div className="relative overflow-hidden rounded-sm">
            <img
              src={PLUMB_IMG}
              alt="New kitchen tap fitted by Teign Gas"
              loading="lazy"
              className="w-full h-[520px] object-cover grayscale-[30%] contrast-125"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 border border-white/15 bg-black/60 backdrop-blur-md px-5 py-4 flex items-center gap-4">
              <div className="flex gap-3 text-torch">
                <Droplets className="w-5 h-5" />
                <Wrench className="w-5 h-5" />
                <ShowerHead className="w-5 h-5" />
              </div>
              <p className="font-mono text-[10px] tracking-[0.25em] text-neutral-300 uppercase">
                All domestic plumbing · No job too small
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
